import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { Icon } from "@/deps/ui-library";
import { useCurrentSession } from "@/app/session";
import { ApprovalReview } from "@/components/ApprovalReview";
import { allows, featureAt, featureNamed, planLine, trialDaysLeft } from "@/app/plan";
import { useSubscription } from "@/app/subscription";
import { LockedFeaturePage } from "@/features/system/LockedFeaturePage";
import { formatDate } from "@/lib/dates";
import { useHelpStatus, useOpenDemo, usePreviewAsRole, useSwitchOrganization } from "@/api/queries";
import type { Permission, RoleName } from "@/api/types";
import { cn } from "@/lib/cn";
import { BarMenu, BarMenuGroup, BarMenuItem } from "./Menu";
import { SubscriptionBanner } from "./SubscriptionBanner";
import { AccountDialogs, type AccountDialog } from "./AccountDialogs";
import { NAV, NEW_DOCUMENT_GROUPS, activeSection, groupsOf, type NavItem } from "./nav";
import { SearchCommandBar, type CommandGroup } from "./SearchCommandBar";
import { useTaxWords } from "@/app/session";

type OpenMenu = null | "org" | "plus" | "user" | "all" | (typeof NAV)[number]["id"];

const ROLES: RoleName[] = ["Administrator", "Accountant", "Verifier", "Approver", "View-only"];

/**
 * The application frame from design/Rigo Accounting.dc.html (lines 19–101):
 * jade top bar with brand, company switcher, section menus, “+ New document”,
 * ⌘K search and the user menu. Content renders below on the canvas.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const say = useTaxWords();
  const { session, can } = useCurrentSession();
  const navigate = useNavigate();
  const { pathname, searchStr } = useLocation();
  const [open, setOpen] = useState<OpenMenu>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const switchOrg = useSwitchOrganization();
  const openDemo = useOpenDemo();
  const [dialog, setDialog] = useState<AccountDialog | null>(null);
  const previewAs = usePreviewAsRole();
  // Ark reports every open and close (trigger, Escape, outside click, a chosen item); the state
  // follows it, one menu at a time.
  const openChange = useCallback(
    (menu: Exclude<OpenMenu, null>) => (next: boolean) =>
      setOpen((current) => (next ? menu : current === menu ? null : current)),
    [],
  );
  const active = activeSection(pathname);
  const subscription = useSubscription()?.status;
  // A route the plan doesn't include shows what it would do instead (suite ADR-0096). The API refuses
  // it too; this is so nobody lands on a page of failures.
  const lockedHere = featureAt(pathname);
  const locked = lockedHere && !allows(subscription, lockedHere.code) ? lockedHere : undefined;
  const lockedPlan = (code?: string) =>
    code && !allows(subscription, code) ? featureNamed(code)?.plan : undefined;
  // Help appears only when the API says it is on (ADR-0010).
  const help = useHelpStatus();

  const go = useCallback(
    (to: string) => {
      setOpen(null);
      // Routes carrying a search string (e.g. /settings?tab=coa) are split here.
      const [path, search] = to.split("?");
      void navigate({
        to: path as never,
        search: search ? (Object.fromEntries(new URLSearchParams(search)) as never) : undefined,
      });
    },
    [navigate],
  );

  // ⌘K / Ctrl+K opens search.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  /** Whether this item is the page on screen. A query string only counts when the item has one. */
  const isCurrent = (item: NavItem) =>
    item.to.includes("?") ? pathname + searchStr === item.to : item.to === pathname;

  const menuItems = (items: NavItem[], prefix = "") =>
    groupsOf(items).map((group, index) => (
      <BarMenuGroup key={`${prefix}${group.label ?? index}`} label={group.label}>
        {group.items.map((it) => (
          <BarMenuItem
            key={say(it.label)}
            value={`${prefix}${say(it.label)}`}
            icon={lockedPlan(it.feature) ? "Lock" : it.icon}
            meta={lockedPlan(it.feature) ?? it.meta}
            active={isCurrent(it)}
            onSelect={() => go(it.to)}
          >
            {say(it.label)}
          </BarMenuItem>
        ))}
      </BarMenuGroup>
    ));

  const searchGroups: CommandGroup[] = useMemo(
    () => [
      {
        label: "Home",
        items: [
          {
            id: "home",
            label: "Financial overview",
            icon: "LayoutDashboard",
            onSelect: () => go("/"),
          },
        ],
      },
      ...NAV.filter((s) => s.items).map((s) => ({
        label: s.label,
        items: (s.items ?? []).map((it) => ({
          id: `${s.id}:${say(it.label)}`,
          label: say(it.label),
          icon: it.icon,
          hint: it.meta,
          onSelect: () => go(it.to),
        })),
      })),
      {
        label: "Create",
        items: NEW_DOCUMENT_GROUPS.flatMap((g) => g.items)
          .filter((it) => !it.permission || can(it.permission as Permission))
          .map((it) => ({
            id: `new:${say(it.label)}`,
            label: say(it.label),
            icon: it.icon,
            onSelect: () => go(it.to),
          })),
      },
    ],
    [can, go, say],
  );

  return (
    <div className="min-h-screen flex flex-col bg-canvas">
      {/* The token focus outline is jade, which is 1.8:1 on this jade-700 bar; white is readable.
          The dropdowns are not affected: RDS portals them out of the bar, onto white panels. */}
      <div className="bg-brand-dark sticky top-0 z-chrome no-print [&_:focus-visible]:outline-white">
        <div className="h-14 flex items-center gap-3 px-4">
          {/* Brand */}
          <button
            type="button"
            onClick={() => go("/")}
            className="flex items-center gap-2 text-white shrink-0 bg-transparent border-0 cursor-pointer p-0"
          >
            <img src="/assets/rigo-icon-compact.svg" alt="" width={26} height={26} />
            <span className="text-[15px] font-bold tracking-[-0.01em] whitespace-nowrap">Rigo</span>
          </button>

          {/* Company switcher */}
          <div className="flex items-center min-w-0">
            <BarMenu
              open={open === "org"}
              onOpenChange={openChange("org")}
              width="lg"
              trigger={
                <button
                  type="button"
                  title="Switch company"
                  className={cn(
                    "flex items-center gap-1.5 max-w-full min-w-0 border-0 rounded-md text-xs font-medium text-white/90 cursor-pointer px-2 py-[5px] hover:bg-white/15 hover:text-white",
                    open === "org" ? "bg-white/15" : "bg-transparent",
                  )}
                >
                  <span className="truncate">{session.organization.name}</span>
                  {session.organization.sampleData ? (
                    // On a phone the bar has no room for the demo's own label, so it rides on the name.
                    <span className="md:hidden shrink-0 rounded-full bg-white/15 px-1.5 text-xs">
                      Demo
                    </span>
                  ) : null}
                  <span className="flex shrink-0 opacity-75">
                    <Icon name="ChevronsUpDown" size="xs" />
                  </span>
                </button>
              }
            >
              <BarMenuGroup label="Switch company">
                {session.organizations.map((o) => (
                  <BarMenuItem
                    key={o.id}
                    value={o.id}
                    onSelect={() => {
                      setOpen(null);
                      switchOrg.mutate(o.id);
                    }}
                  >
                    <span className="flex items-center justify-between gap-2">
                      {o.name}
                      {o.id === session.organization.id && (
                        <span className="flex text-primary">
                          <Icon name="Check" size="sm" />
                          <span className="sr-only"> (current company)</span>
                        </span>
                      )}
                    </span>
                  </BarMenuItem>
                ))}
              </BarMenuGroup>
              {can("settings.company.manage") && !session.organizations.some((o) => o.demo) ? (
                <BarMenuGroup label="Try it out">
                  <BarMenuItem
                    value="demo"
                    icon="FlaskConical"
                    onSelect={() => {
                      setOpen(null);
                      openDemo.mutate(undefined, { onSuccess: () => go("/") });
                    }}
                  >
                    {openDemo.isPending ? "Opening the demo company…" : "Explore the demo company"}
                  </BarMenuItem>
                </BarMenuGroup>
              ) : null}
            </BarMenu>
          </div>

          {session.organization.sampleData ? (
            <DemoPill
              realCompany={session.organizations.find((o) => !o.demo)?.id}
              onSetUp={(id) => switchOrg.mutate(id, { onSuccess: () => go("/setup") })}
            />
          ) : null}

          {/* Section nav */}
          {/* Never narrower than what it holds: on a phone the company name truncates instead, and
              the page does not scroll sideways. */}
          <nav className="flex items-center gap-0.5 relative flex-1 min-w-fit" aria-label="Primary">
            {/* Below 1120px the sections collapse into this one menu. */}
            <div className="min-[1120px]:hidden">
              <BarMenu
                open={open === "all"}
                onOpenChange={openChange("all")}
                width="md"
                trigger={
                  <button type="button" aria-label="Open menu" className={sectionButton(false)}>
                    <Icon name="Menu" size="sm" />
                    Menu
                  </button>
                }
              >
                {NAV.map((s) =>
                  s.items ? (
                    <BarMenuGroup key={s.id} label={s.label}>
                      {s.items.map((it) => (
                        <BarMenuItem
                          key={say(it.label)}
                          value={`all:${s.id}:${say(it.label)}`}
                          icon={it.icon}
                          active={isCurrent(it)}
                          onSelect={() => go(it.to)}
                        >
                          {say(it.label)}
                        </BarMenuItem>
                      ))}
                    </BarMenuGroup>
                  ) : (
                    <BarMenuItem
                      key={s.id}
                      value={`all:${s.id}`}
                      icon={s.icon}
                      active={pathname === s.to}
                      onSelect={() => go(s.to ?? "/")}
                    >
                      {s.label}
                    </BarMenuItem>
                  ),
                )}
              </BarMenu>
            </div>
            {NAV.map((s) => {
              const on = active === s.id;
              const isOpen = open === s.id;
              return (
                <div key={s.id} className="max-[1119px]:hidden">
                  {s.items ? (
                    <BarMenu
                      open={isOpen}
                      onOpenChange={openChange(s.id)}
                      align={s.alignRight ? "right" : "left"}
                      trigger={
                        <button type="button" className={sectionButton(on)}>
                          {s.label}
                          <span className="flex opacity-75">
                            <Icon name={isOpen ? "ChevronUp" : "ChevronDown"} size="xs" />
                          </span>
                        </button>
                      }
                    >
                      {menuItems(s.items)}
                    </BarMenu>
                  ) : (
                    <button
                      type="button"
                      onClick={() => go(s.to ?? "/")}
                      aria-current={on ? "page" : undefined}
                      className={sectionButton(on)}
                    >
                      {s.label}
                    </button>
                  )}
                </div>
              );
            })}

            {/* + New document */}
            <div className="shrink-0 ml-1">
              <BarMenu
                open={open === "plus"}
                onOpenChange={openChange("plus")}
                align="right"
                width="md"
                trigger={
                  <button
                    type="button"
                    title="New document"
                    aria-label="New document"
                    className="w-8 h-8 flex items-center justify-center border-0 rounded-full bg-white/10 text-white/85 cursor-pointer p-0 hover:bg-white/20 hover:text-white"
                  >
                    <Icon name="Plus" size="sm" />
                  </button>
                }
              >
                {NEW_DOCUMENT_GROUPS.map((g) => (
                  <BarMenuGroup key={g.label} label={g.label}>
                    {g.items.map((it) => {
                      const allowed = !it.permission || can(it.permission as Permission);
                      return (
                        <BarMenuItem
                          key={say(it.label)}
                          value={`${g.label}:${say(it.label)}`}
                          icon={lockedPlan(it.feature) ? "Lock" : it.icon}
                          meta={lockedPlan(it.feature)}
                          disabled={!allowed}
                          onSelect={() => go(it.to)}
                        >
                          <span className="flex items-center justify-between gap-2">
                            {say(it.label)}
                            {!allowed && (
                              <>
                                <Icon name="Lock" size="xs" />
                                <span className="sr-only"> (not part of your role)</span>
                              </>
                            )}
                          </span>
                        </BarMenuItem>
                      );
                    })}
                  </BarMenuGroup>
                ))}
              </BarMenu>
            </div>

            <div className="flex-1 min-w-0" />

            <TrialPill
              trialEndsOn={subscription?.messageCode === "trial" ? subscription.trialEndsOn : null}
              plan={subscription?.planCode}
              canSubscribe={can("settings.subscription.manage")}
              onSubscribe={() => go("/settings/subscription/checkout")}
            />

            {/* Search */}
            <button
              type="button"
              title="Search · ⌘K"
              aria-label="Search or jump to, Command K"
              onClick={() => setSearchOpen(true)}
              className="w-8 h-8 shrink-0 flex items-center justify-center border-0 rounded-full bg-white/10 text-white/80 cursor-pointer p-0 hover:bg-white/15"
            >
              <Icon name="Search" size="sm" />
            </button>

            {/* Help — only when the API reports it on */}
            {help.data?.enabled ? (
              <button
                type="button"
                title="Help"
                aria-label="Help"
                onClick={() => go("/help")}
                className={cn(
                  "w-8 h-8 shrink-0 ml-2 flex items-center justify-center border-0 rounded-full cursor-pointer p-0 hover:bg-white/15",
                  pathname.startsWith("/help")
                    ? "bg-white/20 text-white"
                    : "bg-white/10 text-white/80",
                )}
              >
                <Icon name="LifeBuoy" size="sm" />
              </button>
            ) : null}

            {/* User */}
            <div className="shrink-0 ml-2">
              <BarMenu
                open={open === "user"}
                onOpenChange={openChange("user")}
                align="right"
                width="md"
                trigger={
                  <button
                    type="button"
                    title={session.user.role}
                    aria-label={`${session.user.fullName}, ${session.user.role}`}
                    className="w-[30px] h-[30px] rounded-full bg-brand border border-white/30 flex items-center justify-center text-xs font-semibold text-white cursor-pointer p-0"
                  >
                    {session.user.initials}
                  </button>
                }
              >
                <div className="p-2 border-b border-border mb-1">
                  <div className="text-sm font-semibold text-foreground">
                    {session.user.fullName}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {session.user.role} · {session.user.email}
                  </div>
                  {planLine(subscription, formatDate) ? (
                    <div className="text-xs text-muted-foreground mt-1">
                      {planLine(subscription, formatDate)}
                    </div>
                  ) : null}
                </div>
                {subscription?.enabled ? (
                  <BarMenuItem
                    value="subscription"
                    icon="CreditCard"
                    onSelect={() => go("/settings/subscription")}
                  >
                    Subscription
                  </BarMenuItem>
                ) : null}
                <BarMenuItem
                  value="profile"
                  icon="User"
                  onSelect={() => {
                    setOpen(null);
                    setDialog("profile");
                  }}
                >
                  Profile
                </BarMenuItem>
                <BarMenuItem
                  value="password"
                  icon="KeyRound"
                  onSelect={() => {
                    setOpen(null);
                    setDialog("password");
                  }}
                >
                  Change password
                </BarMenuItem>
                {import.meta.env.VITE_API_MODE !== "http" && (
                  <BarMenuGroup label="Preview as role (mock only)">
                    {ROLES.map((r) => (
                      <BarMenuItem
                        key={r}
                        value={`role:${r}`}
                        active={r === session.user.role}
                        onSelect={() => {
                          setOpen(null);
                          previewAs.mutate(r);
                        }}
                      >
                        {r}
                      </BarMenuItem>
                    ))}
                  </BarMenuGroup>
                )}
                <BarMenuItem
                  value="signout"
                  icon="LogOut"
                  tone="danger"
                  onSelect={() => {
                    setOpen(null);
                    setDialog("signout");
                  }}
                >
                  Sign out
                </BarMenuItem>
              </BarMenu>
            </div>
          </nav>
        </div>
      </div>

      {/* Below the bar and above everything else: the one place a billing state is announced
          (ADR-0009). It renders nothing at all in the ordinary case. */}
      <SubscriptionBanner />

      <AccountDialogs dialog={dialog} onClose={() => setDialog(null)} />

      <SearchCommandBar
        open={searchOpen}
        placeholder="Search or jump to…"
        groups={searchGroups}
        onClose={() => setSearchOpen(false)}
      />

      {/* The frame every screen sits in: the padding and width a planned screen used to give itself
          and the real ones never had. */}
      <main className="box-border w-full max-w-[1280px] min-w-0 flex-1 px-6 py-5">
        {locked ? (
          <LockedFeaturePage feature={locked} />
        ) : (
          <ApprovalReview>{children}</ApprovalReview>
        )}
      </main>

      <footer className="no-print px-6 py-3 text-xs text-fg-hint flex items-center gap-2">
        <span>{session.organization.name}</span>
        <span>·</span>
        {session.fiscalYear && (
          <>
            <span>{session.fiscalYear.label}</span>
            <span>·</span>
          </>
        )}
        <span>{session.organization.currency}</span>
        <span className="flex-1" />
        {import.meta.env.VITE_API_MODE !== "http" && (
          <span className="num">mock data — the figures are invented</span>
        )}
      </footer>
    </div>
  );
}

/**
 * The trial's place in the bar: the plan and the days left, amber in the last three, with Subscribe for
 * whoever can (suite ADR-0096). Nothing outside a trial.
 */
function TrialPill({
  trialEndsOn,
  plan,
  canSubscribe,
  onSubscribe,
}: {
  trialEndsOn: string | null | undefined;
  plan: string | null | undefined;
  canSubscribe: boolean;
  onSubscribe: () => void;
}) {
  if (!trialEndsOn) return null;
  const left = trialDaysLeft(trialEndsOn);
  const ending = left <= 3;
  const name =
    plan === "PRO" ? "Pro" : plan === "STANDARD" ? "Standard" : plan === "STARTER" ? "Starter" : "";
  return (
    <div
      role="status"
      className={cn(
        // A phone's bar has no room for it; the trial's banner and Settings › Subscription still say it.
        "max-sm:hidden flex items-center gap-2 shrink-0 rounded-full px-3 py-1 text-xs font-medium mr-2",
        ending ? "bg-warn-soft text-warn-text" : "bg-white/15 text-white",
      )}
    >
      <span className="whitespace-nowrap">
        {`${name} trial · ${left} ${left === 1 ? "day" : "days"} left`}
      </span>
      {canSubscribe ? (
        // rds-check-ok: a control of the jade top bar, styled for it like the bar's others.
        <button
          type="button"
          onClick={onSubscribe}
          className="border-0 bg-transparent p-0 text-xs font-semibold text-current underline cursor-pointer"
        >
          Subscribe
        </button>
      ) : null}
    </div>
  );
}

/**
 * The demo company's place in the bar (ADR-0063): it says the books are sample data, and takes the person to their own
 * company's setup when they're ready. Nothing moves from the demo to their books.
 */
function DemoPill({
  realCompany,
  onSetUp,
}: {
  realCompany: string | undefined;
  onSetUp: (organisationId: string) => void;
}) {
  return (
    <div
      role="status"
      className="max-md:hidden flex items-center gap-2 shrink-0 rounded-full px-3 py-1 text-xs font-medium bg-white/15 text-white"
    >
      <span className="whitespace-nowrap">Demo company · sample data</span>
      {realCompany ? (
        // rds-check-ok: a control of the jade top bar, styled for it like the bar's others.
        <button
          type="button"
          onClick={() => onSetUp(realCompany)}
          className="border-0 bg-transparent p-0 text-xs font-semibold text-current underline cursor-pointer whitespace-nowrap"
        >
          Set up my company
        </button>
      ) : null}
    </div>
  );
}

/** A section's button in the bar: the current section is washed and bold. */
function sectionButton(on: boolean) {
  return cn(
    "flex items-center gap-[3px] px-[9px] py-[7px] border-0 rounded-md text-sm cursor-pointer whitespace-nowrap hover:bg-white/12 hover:text-white",
    on ? "bg-white/15 text-white font-semibold" : "bg-transparent text-white/80 font-normal",
  );
}
